import { buildBackendUrl } from '../config';
const MENU_STORAGE_KEY = 'textile_erp_menus';
const TOKEN_KEY = 'textile_erp_token';
/**
 * Get menus from localStorage
 */
function getStoredMenus() {
    try {
        const stored = localStorage.getItem(MENU_STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
        return null;
    }
    catch (error) {
        console.error('Failed to get stored menus:', error);
        return null;
    }
}
/**
 * Store menus in localStorage
 */
function storeMenus(data) {
    try {
        localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(data));
    }
    catch (error) {
        console.error('Failed to store menus:', error);
    }
}
/**
 * Clear stored menus (call on logout)
 */
export function clearStoredMenus() {
    localStorage.removeItem(MENU_STORAGE_KEY);
    // Reset the singleton state
    menuServiceState.data = null;
    menuServiceState.isLoading = false;
    menuServiceState.fetchPromise = null;
}
// Singleton state to prevent multiple API calls
const menuServiceState = {
    data: null,
    isLoading: false,
    fetchPromise: null,
};
/**
 * Fetch menus from API (internal - only called once)
 */
async function fetchMenusFromApi() {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(buildBackendUrl('/api/identity/users/me/menus'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch menus: ${response.status}`);
    }
    const result = await response.json();
    // API returns { menus, paths, scopes, pathToMenuName, pathToMenuId, isAdmin }
    return {
        menus: result.menus || [],
        paths: result.paths || [],
        scopes: result.scopes || {},
        pathToMenuId: result.pathToMenuId || {},
        pathToMenuName: result.pathToMenuName || {},
        isAdmin: result.isAdmin || false,
    };
}
/**
 * Check if user has a valid token
 */
function hasValidToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    return !!token && token.length > 0;
}
/**
 * Get menus - returns cached data or fetches from API once
 * If menus are missing but user has valid token, fetches from API automatically
 */
export async function getMenus(forceRefresh = false) {
    // If force refresh, clear cache
    if (forceRefresh) {
        menuServiceState.data = null;
        menuServiceState.fetchPromise = null;
        localStorage.removeItem(MENU_STORAGE_KEY);
    }
    // Return cached data if available
    if (menuServiceState.data) {
        return menuServiceState.data;
    }
    // Try to get from localStorage
    const storedData = getStoredMenus();
    if (storedData && storedData.menus && storedData.menus.length > 0) {
        menuServiceState.data = storedData;
        return storedData;
    }
    // No cached data available - check if user has valid token
    if (!hasValidToken()) {
        // No token, return empty data
        return { menus: [], paths: [], scopes: {}, pathToMenuId: {} };
    }
    // If already fetching, wait for the existing promise
    if (menuServiceState.fetchPromise) {
        return menuServiceState.fetchPromise;
    }
    // User has valid token but no menus - fetch from API
    menuServiceState.isLoading = true;
    menuServiceState.fetchPromise = fetchMenusFromApi()
        .then(data => {
        // Store in memory and localStorage
        menuServiceState.data = data;
        storeMenus(data);
        return data;
    })
        .catch(error => {
        console.error('Failed to fetch menus:', error);
        throw error;
    })
        .finally(() => {
        menuServiceState.isLoading = false;
        menuServiceState.fetchPromise = null;
    });
    return menuServiceState.fetchPromise;
}
/**
 * Get accessible paths only (for route protection)
 */
export async function getAccessiblePaths(forceRefresh = false) {
    const data = await getMenus(forceRefresh);
    return data.paths;
}
/**
 * Get user scopes (for permission checking)
 */
export async function getUserScopes(forceRefresh = false) {
    const data = await getMenus(forceRefresh);
    return data.scopes;
}
/**
 * Check if user has a specific scope for a menu
 */
export async function hasScope(menuName, scopeName, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    return scopes[menuName]?.includes(scopeName) ?? false;
}
/**
 * Check if user has any of the specified scopes for a menu
 */
export async function hasAnyScope(menuName, scopeNames, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    const menuScopes = scopes[menuName] || [];
    return scopeNames.some(name => menuScopes.includes(name));
}
/**
 * Check if user has all of the specified scopes for a menu
 */
export async function hasAllScopes(menuName, scopeNames, forceRefresh = false) {
    const scopes = await getUserScopes(forceRefresh);
    const menuScopes = scopes[menuName] || [];
    return scopeNames.every(name => menuScopes.includes(name));
}
/**
 * Check if menus are currently loading
 */
export function isMenusLoading() {
    return menuServiceState.isLoading;
}
/**
 * Refresh menus from API (force fetch)
 */
export async function refreshMenus() {
    return getMenus(true);
}
//# sourceMappingURL=menu.service.js.map